'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, Text, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';

//* Styles
import FooterStyle from './style';

//* Hooks
import { withDataContext } from '@/context';

//* Helpers
import { config } from '@/helpers';

//* Utils
import { facebookSVG, instagramSVG, linkedinSVG } from './utils';

//* Components
import LottieAnimation from '../LottieAnimation';

//* Lottie files
import lottie1 from '@/lottie/cat1.json';
import lottie2 from '@/lottie/cat2.json';

gsap.registerPlugin(ScrollTrigger);

const Footer = ({ globalData }) => {
	const pathname = usePathname();
	const { translate } = useTranslation();
	const { winWidth } = useUi();

	const triggerRef = useRef();
	const contentRef = useRef();
	const leftWrapRef = useRef();
	const rightWrapRef = useRef();
	const footerSocialsRef = useRef();
	const textRef1 = useRef();
	const textRef2 = useRef();
	const textRef3 = useRef();
	const textRef4 = useRef();

	const [isStartSecond, setIsStartSecond] = useState(false);
	const [playLottie, setPlayLottie] = useState(false);
	const [resizeTarget, setResizeTarget] = useState(null);

	const resize = useResizeObserver(resizeTarget);

	//! Checks
	const isPathnameResizeDetect = useMemo(() => {
		return pathname.includes(config.routes.contactUs.path) ? null : resize;
	}, [resize, pathname]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setResizeTarget(document.body);
		}
	}, []);

	useEffect(() => {
		if (!contentRef.current || !leftWrapRef.current || !rightWrapRef.current || !footerSocialsRef.current) {
			return;
		}

		const getChildren = (ref) => (ref.current instanceof Element ? Array.from(ref.current.children) : []);

		const contentChildren = getChildren(contentRef);
		const leftChildren = getChildren(leftWrapRef);
		const rightChildren = getChildren(rightWrapRef);
		const socialsChildren = getChildren(footerSocialsRef);

		const textElements = [textRef1, textRef2, textRef3, textRef4].map((ref) => ref.current).filter((el) => el instanceof Element);

		const allTargets = [...contentChildren, ...leftChildren, ...rightChildren, ...socialsChildren, ...textElements];

		triggerRef.current = gsap.to('.footer-inner-wrapper', {
			scrollTrigger: {
				trigger: 'footer',
				start: 'top bottom',
				end: 'bottom bottom',
				scrub: true,
				onEnter: () => {
					setPlayLottie(true);

					gsap.killTweensOf(allTargets);
					gsap.fromTo(
						allTargets,
						{ opacity: 0 },
						{
							opacity: 1,
							ease: 'power1.in',
							duration: 0.5,
							stagger: 0.02,
						}
					);
				},
				onLeaveBack: () => {
					setPlayLottie(false);
					setIsStartSecond(false);

					gsap.killTweensOf(allTargets);
					gsap.set(allTargets, { opacity: 0 });
				},
			},
			rotateZ: '0deg',
			y: '0px',
			duration: 1,
			ease: 'power1.out',
		});

		return () => {
			if (triggerRef.current) {
				triggerRef.current.scrollTrigger?.kill();
				triggerRef.current.kill?.();
				gsap.killTweensOf(allTargets);
			}
		};
	}, [isPathnameResizeDetect]);

	const footerStoreLeft = useMemo(() => {
		return Object.entries(config.routes)
			.slice(0, 4)
			.filter(([, val]) => val.isFooter)
			.map(([key, val]) => (
				<Link
					key={key}
					url={val.path}
					className='footer-menu-item-link'
				>
					<Text
						text={val.name}
						className='h6 font-fss-regular green-1000 footer-menu-item'
					/>
				</Link>
			));
	}, []);

	const footerStoreRight = useMemo(() => {
		const rightKeys = ['recipes', 'blog', 'contactUs', 'faq'];

		return Object.entries(config.routes)
			.filter(([key, val]) => rightKeys.includes(key) && val.isFooter)
			.map(([key, val]) => (
				<Link
					key={key}
					url={val.path}
					className='footer-menu-item-link'
				>
					<Text
						text={val.name}
						className='h6 font-fss-regular green-1000 footer-menu-item'
					/>
				</Link>
			));
	}, []);

	return (
		<FooterStyle className='footer-wrapper'>
			<div className='footer-inner-wrapper'>
				<div className='footer-inner-top-wrapper'>
					<div
						ref={contentRef}
						className='footer-left-content'
					>
						<Text
							text={'needSomeHelpWithSymbol'}
							className={`h3 font-larken-regular green-1000 footer-left-text1`}
						/>

						<Link
							url={config.routes.contactUs.path}
							className='footer-left-text2-link'
						>
							<Text
								text={'getInTouch'}
								className={`h3 font-larken-medium-italic green-1000 footer-left-text2 text-underline-animation`}
							/>
						</Link>
					</div>

					<div className='footer-right-content'>
						<div className='content-col-left'>
							<div
								ref={leftWrapRef}
								className='content-col-left-inner'
							>
								{footerStoreLeft}
							</div>
						</div>

						<div className='content-col-center'>
							<div
								ref={rightWrapRef}
								className='content-col-center-inner'
							>
								{footerStoreRight}
							</div>
						</div>

						<div
							ref={footerSocialsRef}
							className='content-col-right'
						>
							{globalData?.socials ? (
								<div className='content-col-right-inner'>
									<div className='footer-icons-wrapper'>
										{globalData.socials?.facebook && (
											<Link
												external
												url={globalData.socials.facebook}
											>
												<div className='icon-wrap'>{facebookSVG}</div>
											</Link>
										)}
										{globalData.socials?.instagram && (
											<Link
												external
												url={globalData.socials.instagram}
											>
												<div className='icon-wrap'>{instagramSVG}</div>
											</Link>
										)}
										{globalData.socials?.linkedin && (
											<Link
												external
												url={globalData.socials.linkedin}
											>
												<div className='icon-wrap'>{linkedinSVG}</div>
											</Link>
										)}
									</div>
								</div>
							) : null}
						</div>
					</div>
				</div>

				<div className='footer-inner-bottom-wrapper'>
					<div className='footer-left-content'>
						<Text
							ref={textRef1}
							className='p2 green-1000 font-fss-regular'
						>
							©{new Date().getFullYear()} Ora
						</Text>

						{winWidth >= 768 ? (
							<div className='lottie-container'>
								<div className={`lottie-second ${!isStartSecond ? 'un-active-lottie' : ''}`}>
									<LottieAnimation
										autoplay={false}
										isPlay={isStartSecond}
										isReverse={false}
										loop={isStartSecond}
										animData={lottie2}
										render='svg'
									/>
								</div>
								<div className={`lottie-first ${!isStartSecond ? '' : 'un-active-lottie'}`}>
									<LottieAnimation
										key={playLottie + 's'}
										autoplay={false}
										isPlay={playLottie}
										isReverse={false}
										loop={false}
										animData={lottie1}
										render='svg'
										onEnd={() => setIsStartSecond(true)}
									/>
								</div>
							</div>
						) : (
							<>
								<div className='content-col-left'>
									<Link
										ref={textRef2}
										url={config.routes.termsOfUse.path}
										className='terms-of-use-link text-underline-animation'
									>
										<Text
											text={config.routes.termsOfUse.name}
											className='p2 font-fss-regular green-1000'
										/>
									</Link>
								</div>

								<div className='content-col-center'>
									<Link
										ref={textRef3}
										url={config.routes.privacyPolicy.path}
										className='privacy-policy-link text-underline-animation'
									>
										<Text
											text={config.routes.privacyPolicy.name}
											className='p2 font-fss-regular green-1000'
										/>
									</Link>

									<Text
										ref={textRef4}
										className='p2 font-fss-regular green-1000'
									>
										{translate('madeBy')}&nbsp;
										<Link
											external
											url='https://conceptstudio.com/'
											className='office-name-link text-underline-animation'
										>
											<span className='footer-office-name'>Concept Studio</span>
										</Link>
									</Text>
								</div>
							</>
						)}
					</div>

					<div className='footer-right-content'>
						{winWidth < 768 ? (
							<div className='lottie-container'>
								<div className={`lottie-second ${!isStartSecond ? 'un-active-lottie' : ''}`}>
									<LottieAnimation
										autoplay={false}
										isPlay={isStartSecond}
										isReverse={false}
										loop={isStartSecond}
										animData={lottie2}
										render='svg'
									/>
								</div>
								<div className={`lottie-first ${!isStartSecond ? '' : 'un-active-lottie'}`}>
									<LottieAnimation
										key={playLottie + 's'}
										autoplay={false}
										isPlay={playLottie}
										isReverse={false}
										loop={false}
										animData={lottie1}
										render='svg'
										onEnd={() => setIsStartSecond(true)}
									/>
								</div>
							</div>
						) : (
							<>
								<div className='content-col-left'>
									<Link
										ref={textRef2}
										url={config.routes.termsOfUse.path}
										className='terms-of-use-link text-underline-animation'
									>
										<Text
											text={config.routes.termsOfUse.name}
											className='p2 font-fss-regular green-1000'
										/>
									</Link>
								</div>

								<div className='content-col-center'>
									<Link
										ref={textRef3}
										url={config.routes.privacyPolicy.path}
										className='privacy-policy-link text-underline-animation'
									>
										<Text
											text={config.routes.privacyPolicy.name}
											className='p2 font-fss-regular green-1000'
										/>
									</Link>

									<Text
										ref={textRef4}
										className='p2 font-fss-regular green-1000'
									>
										{translate('madeBy')}&nbsp;
										<Link
											external
											url='https://conceptstudio.com/'
											className='office-name-link text-underline-animation'
										>
											<span className='footer-office-name'>Concept Studio</span>
										</Link>
									</Text>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</FooterStyle>
	);
};

export default withDataContext(Footer, ['globalData']);
